import * as React from "react";
import { AlertCircle, Armchair, MoreHorizontal, Pencil, RefreshCw, Search, X } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@eduviaos/ui-kit";
import { useCanEditSeating } from "@/hooks/useSeatingAuthoring";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { SeatEditorSheet } from "@/components/seating/SeatEditorSheet";
import { useSeats, type Seat, type SeatListFilters } from "@/lib/seating/seats";
import { useZones } from "@/lib/seating/zones";

/**
 * Phase F-finale-7 Slice 7b.4b — IA seat-management Seats surface.
 *
 * The second piece of the study-centre seat-management module (after Zones,
 * 7b.4a): a `<DataTable>` (Iron Rule #16) of the tenant's seats over the
 * real-auth `/api/seats` surface, filterable by zone, with create + edit
 * through `<SeatEditorSheet>`. Seats hang off zones — the zone filter and
 * the editor's zone picker both read from `/api/zones`.
 *
 * Write affordances ("Add seat", the per-row edit action) are gated on
 * `useCanEditSeating` (`manage_zones`) — identical gating to the Zones
 * surface. The BE re-checks `MANAGE_ZONES` on every call, so this is a UX
 * layer, not the only line of defence.
 */

const ANY_ZONE = "__any__";

const ACTIVE_FILTER_OPTIONS = [
  { value: "__any__", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
] as const;

type ActiveFilter = (typeof ACTIVE_FILTER_OPTIONS)[number]["value"];

type DialogState = { kind: "none" } | { kind: "create" } | { kind: "edit"; seat: Seat };

export function SeatsPage() {
  const { canEditSeating, missingRbac } = useCanEditSeating();

  const [qInput, setQInput] = React.useState("");
  const [zoneFilter, setZoneFilter] = React.useState<string>(ANY_ZONE);
  const [activeFilter, setActiveFilter] = React.useState<ActiveFilter>("__any__");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "seating-seats",
    defaultPageSize: 10,
  });
  const [dialog, setDialog] = React.useState<DialogState>({ kind: "none" });

  // All zones (up to the BE cap) power the filter dropdown, the row-level
  // zone-name resolution, and the editor's zone picker.
  const zonesQuery = useZones({ limit: 200 });
  const zones = React.useMemo(() => zonesQuery.data?.items ?? [], [zonesQuery.data]);
  const zoneNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const z of zones) map.set(z.id, z.name);
    return map;
  }, [zones]);

  const filters = React.useMemo<SeatListFilters>(
    () => ({
      zone_id: zoneFilter === ANY_ZONE ? undefined : zoneFilter,
      is_active: activeFilter === "__any__" ? undefined : activeFilter === "active",
      limit: pageSize,
      offset: page * pageSize,
    }),
    [zoneFilter, activeFilter, page, pageSize],
  );

  const query = useSeats(filters);
  const { data, isLoading, isFetching, isError, error, refetch } = query;

  const total = data?.total ?? 0;
  const allItems = React.useMemo(() => data?.items ?? [], [data]);

  const filteredItems = React.useMemo(() => {
    const needle = qInput.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((s) =>
      [s.seat_number, s.seat_type, zoneNameById.get(s.zone_id) ?? s.zone_id]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [allItems, qInput, zoneNameById]);

  const hasFiltersApplied = qInput !== "" || zoneFilter !== ANY_ZONE || activeFilter !== "__any__";

  const clearFilters = React.useCallback(() => {
    setQInput("");
    setZoneFilter(ANY_ZONE);
    setActiveFilter("__any__");
    setPage(0);
  }, []);

  const createDefaultZoneId = zoneFilter === ANY_ZONE ? null : zoneFilter;

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Seats</PageHeaderHeading>
            <PageHeaderDescription>
              Individual seats within your zones. Members book or are assigned to these seats.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac ? (
        <Card data-testid="seating-missing-rbac-notice" className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">View-only access</CardTitle>
            <CardDescription>
              You don&apos;t have the <span className="font-medium">Manage zones</span> permission,
              so seats are read-only. Ask an administrator to grant it if you need to make changes.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn&apos;t load seats
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable>
        <DataTableToolbar>
          <div className="relative w-full sm:w-64">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search number, type or zone…"
              className="pl-8"
              aria-label="Search seats"
            />
          </div>
          <Select
            value={zoneFilter}
            onValueChange={(v) => {
              setZoneFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-44" aria-label="Zone filter">
              <SelectValue placeholder="Any zone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_ZONE}>Any zone</SelectItem>
              {zones.map((z) => (
                <SelectItem key={z.id} value={z.id}>
                  {z.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={activeFilter}
            onValueChange={(v) => {
              setActiveFilter(v as ActiveFilter);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-36" aria-label="Status filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIVE_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFiltersApplied ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
          {canEditSeating ? (
            <Button
              size="sm"
              onClick={() => setDialog({ kind: "create" })}
              data-testid="seats-add-button"
            >
              <Armchair aria-hidden="true" className="size-4" />
              Add seat
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[22%]">Seat</TableHead>
                <TableHead className="w-[34%]">Zone</TableHead>
                <TableHead className="w-[24%]">Type</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                <TableHead className="w-[8%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={5} />
            ) : (
              <TableBody>
                {filteredItems.map((s) => (
                  <TableRow key={s.id} data-testid={`seat-row-${s.id}`}>
                    <TableCell className="font-medium">{s.seat_number}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {zoneNameById.get(s.zone_id) ?? (
                        <span className="font-mono text-xs">{s.zone_id}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {s.seat_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        status={s.is_active ? "active" : "inactive"}
                        label={s.is_active ? "Active" : "Inactive"}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {canEditSeating ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for seat ${s.seat_number}`}
                              data-testid={`seat-row-actions-${s.id}`}
                              className="size-7"
                            >
                              <MoreHorizontal aria-hidden="true" className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onSelect={() => setDialog({ kind: "edit", seat: s })}
                              data-testid={`seat-action-edit-${s.id}`}
                            >
                              <Pencil aria-hidden="true" className="size-4" />
                              Edit
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && filteredItems.length === 0 ? (
          <DataTableEmpty
            icon={<Armchair aria-hidden="true" />}
            title={hasFiltersApplied ? "No matches" : "No seats yet"}
            description={
              hasFiltersApplied
                ? "Adjust filters or clear them to see every seat."
                : "Add seats to your zones so members can book or be assigned to them."
            }
            action={
              hasFiltersApplied ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : canEditSeating ? (
                <Button size="sm" onClick={() => setDialog({ kind: "create" })}>
                  <Armchair aria-hidden="true" className="size-4" />
                  Add seat
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && filteredItems.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {filteredItems.length.toLocaleString()} of {total.toLocaleString()}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="seats-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {canEditSeating ? (
        <SeatEditorSheet
          open={dialog.kind === "create" || dialog.kind === "edit"}
          onOpenChange={(next) => {
            if (!next) setDialog({ kind: "none" });
          }}
          zones={zones}
          existing={dialog.kind === "edit" ? dialog.seat : null}
          defaultZoneId={createDefaultZoneId}
          onSaved={() => setDialog({ kind: "none" })}
        />
      ) : null}
    </div>
  );
}
